import React, {useRef, useState} from "react";
import {
	QuestionContainer,
	QuestionCounter,
	QuestionTitle,
	QuestionTitleContainer,
	QuestionWrapper,
} from "./common";
import styled from "styled-components";
import {Exist} from "components/Exist";
import {useSlider, useStore} from "modules/hooks";
import {IMAGES_URL} from "modules/constants";
import {IconArrow} from "components/Arrow";
import {useMediaQuery} from "@mui/material";
import {QuestionLogo} from "./Logo";
import {IQuestionProps} from "modules/types/question";
import world_vision from "assets/images/world_vision.png";

interface IWrapperProps {
	background: null | string;
	backgroundMobile?: null | string;
}

const getWrapperBackground = ({background}: IWrapperProps) =>
	background
		? `url(${IMAGES_URL}image/${background}) no-repeat center center / cover`
		: `linear-gradient(180deg, #000000 0%, #1d1d1d 30%, #000000 100%);`;

const Wrapper = styled(QuestionWrapper)<IWrapperProps>`
	background: ${getWrapperBackground};

	@media (max-width: 840px) {
		background: ${({background, backgroundMobile}) =>
			getWrapperBackground({background: backgroundMobile || background})};
	}
`;

const OptionsList = styled.div`
	position: relative;
	display: flex;
	width: 100%;
	overflow: hidden;
	scroll-snap-type: x mandatory;

	@media (max-width: 1000px) {
		flex-wrap: wrap;
		justify-content: space-between;
	}
`;

const Image = styled.img`
	width: 100%;
	height: auto;
	object-fit: cover;
	flex: 1;
`;

const FigureCaption = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 10px;
	font-family: "Khand", sans-serif;
	font-weight: 700;
	font-size: 16px;
	line-height: 18px;
	transition: background 0.5s;
	background-size: 0 1px;
	min-height: 54px;

	@media (max-width: 500px) {
		min-height: 48px;
		font-size: 14px;
		line-height: 14px;
		padding: 10px 4px;
	}

	.last-question & {
		word-spacing: 4px;
		line-height: 140%;
	}
`;

const Option = styled.div`
	display: flex;
	flex-direction: column;
	text-align: center;
	margin-right: 20px;
	cursor: pointer;
	transition: all 0.5s;
	min-width: 200px;
	width: 100%;
	color: ${({theme}) => theme.color.question1};
	background: ${({theme}) => theme.background.question1};

	@media (max-width: 1400px) {
		min-width: 183px;
	}

	@media (max-width: 1000px) {
		margin-right: 0;
		min-width: 120px;
		width: calc(50% - 8px);
		margin-bottom: 20px;
	}

	&:last-child {
		margin-right: 0;
	}

	&.active {
		${FigureCaption} {
			background-size: 100% 1px;
		}
	}

	&.fade {
		position: relative;

		&:after {
			content: "";
			position: absolute;
			left: 0;
			top: 0;
			width: 100%;
			height: 100%;
			animation: fadeIn 0.5s ease-in-out forwards;

			@keyframes fadeIn {
				from {
					background-color: rgba(0, 0, 0, 0);
				}

				to {
					background-color: rgba(0, 0, 0, 0.7);
				}
			}
		}
	}
`;

const WrapperScrollNaw = styled.div`
	display: flex;
	justify-content: flex-end;
	align-items: center;
	margin-top: 10px;
`;

const ScrollButton = styled.button`
	background: #fff;
	border: none;
	width: 40px;
	height: 40px;
	cursor: pointer;

	&:disabled {
		background: #555555;
	}

	${IconArrow} {
		font-size: 11px;
		margin-left: 10px;

		&:before,
		&:after {
			background: #000000;
		}
	}
`;

const ScrollRight = styled(ScrollButton)`
	margin-left: 10px;

	${IconArrow} {
		transform: rotate(-88deg);
	}
`;

const ScrollLeft = styled(ScrollButton)`
	${IconArrow} {
		margin-left: -10px;
		transform: rotate(88deg);
	}
`;

const ShortTitle = styled.h4`
	font-size: 22px;
	display: inline;
	line-height: 1.6;
	span {
		white-space: nowrap;
	}
	img {
		max-height: 1.6em;
		margin: 0 4px;
		display: inline;
		vertical-align: top;
		transform: translateY(-8px);
	}
	& + div {
		margin-top: 24px;
	}
`;
const getStateClass = (currentID: number, selectedID: number | null) => {
	if (selectedID === null) {
		return "";
	}

	if (currentID === selectedID) {
		return "active";
	}

	return "fade";
};

export const QuestionGeneral: React.FC<IQuestionProps> = ({
	title,
	options,
	id: questionID,
	background,
	questionNumber,
	background_mobile,
	isLast,
}) => {
	const {UIQuestionsProgress, I18n} = useStore();
	const [selectedID, setSelectedID] = useState<null | number>(null);
	const element = useRef<HTMLDivElement>(null);
	const isMobile = useMediaQuery("(max-width: 1000px)");
	const slider = useSlider(element);
	const isSliderVisible = Boolean(!isMobile && slider && slider.is_show_nav);
	const noOfQuestions = UIQuestionsProgress.noOfQuestions;

	return (
		<Wrapper
			background={background}
			backgroundMobile={background_mobile}
			className={isLast ? "last-question" : ""}>
			<QuestionContainer>
				<QuestionLogo />
				{isLast && (
					<ShortTitle>
						{I18n.t("screens.last_question.title_part1")}
						<img src={world_vision} alt="World vision logo" />
						{I18n.t("screens.last_question.title_part2")}
					</ShortTitle>
				)}
				<QuestionTitleContainer>
					<QuestionCounter>
						Q{questionNumber}/{noOfQuestions}
					</QuestionCounter>
					<QuestionTitle>{title}</QuestionTitle>
				</QuestionTitleContainer>

				<OptionsList ref={element}>
					{options.map(({id, title, image, mobile_image}) => (
						<Option
							key={id}
							className={getStateClass(id, selectedID)}
							onClick={() => {
								UIQuestionsProgress.selectAnswer(questionID, id);
								setSelectedID(id);
							}}>
							<Exist when={isMobile && Boolean(mobile_image)}>
								<Image src={`${IMAGES_URL}image/${mobile_image}`} />
							</Exist>
							<Exist when={!isMobile && Boolean(image)}>
								<Image src={`${IMAGES_URL}image/${image}`} />
							</Exist>
							<FigureCaption>{title}</FigureCaption>
						</Option>
					))}
				</OptionsList>
				<Exist when={isSliderVisible}>
					<WrapperScrollNaw>
						<ScrollLeft
							onClick={slider?.scrollLeft}
							disabled={slider?.is_disabled_left}>
							<IconArrow />
						</ScrollLeft>
						<ScrollRight
							onClick={slider?.scrollRight}
							disabled={slider?.is_disabled_right}>
							<IconArrow />
						</ScrollRight>
					</WrapperScrollNaw>
				</Exist>
			</QuestionContainer>
		</Wrapper>
	);
};
